import { AclProvider } from '@/features/acl/acl-provider';
import { DialogProvider } from '@/features/dialog/dialog-provider';
import { AllDrawers, AllDrawersProvider } from '@/features/drawers/all-drawers';
import { cn } from '@/lib/utils';
import { useEffect, useState, type ReactNode } from 'react';
import { Outlet, useMatches } from 'react-router-dom';
import { useLocalStorage } from 'usehooks-ts';
import { FullWidthNav } from './full-width-nav';
import { GlobalSearchBarProvider } from './global-search-bar-provider';
import { IconOnlyNav } from './icon-only-nav';
import { MobileNav } from './mobile-nav';
export { NavPathOverrideEnum } from '../contracts';

export type RouteHandle = {
    disableDrawers?: boolean;
};

const PlatoUserLayoutImplementation = (): ReactNode => {
    const matches = useMatches();
    const [smallScreenDrawOpen, setSmallScreenDrawOpen] = useState(false);
    const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);

    const [desktopSidebarOpen, setDesktopSidebarOpen] = useLocalStorage<boolean>(
        'desktop-sidebar-open',
        true,
    );

    const disableDrawers = matches.some((match) => {
        const handle = match.handle as RouteHandle | undefined;
        return handle?.disableDrawers === true;
    });

    const closeDrawer = () => {
        setSmallScreenDrawOpen(false);
        setMobileDrawerOpen(false);
    };

    // Close mobile drawer on orientation change
    useEffect(() => {
        const orientation = screen.orientation;

        if (!orientation) return;

        const handleOrientationChange = () => {
            closeDrawer();
        };

        orientation.addEventListener('change', handleOrientationChange);
        return () => {
            orientation.removeEventListener('change', handleOrientationChange);
        };
    }, []);

    return (
        <GlobalSearchBarProvider
            onBeforeOpenSearch={closeDrawer}
            needsToCloseOtherWindowBeforeOpeningSearch={smallScreenDrawOpen}
        >
            <div className="z-30 h-full">
                {/* Side navigation bars */}
                <div className="fixed z-30 hidden h-full w-[52px] bg-neutral-50 sm:inset-y-0 sm:left-0 sm:block xl:hidden">
                    <IconOnlyNav
                        isDesktopScreenSize={false}
                        smallScreenDrawOpen={smallScreenDrawOpen}
                        setSmallScreenDrawOpen={setSmallScreenDrawOpen}
                        setDesktopSidebarOpen={setDesktopSidebarOpen}
                        closeDrawer={closeDrawer}
                    />
                </div>
                {/* full desktop sidebar */}
                <div
                    className={cn(
                        'z-30 hidden h-full bg-neutral-50 xl:fixed xl:inset-y-0 xl:left-0 xl:block',
                        desktopSidebarOpen ? 'xl:w-[250px]' : 'xl:w-[52px]',
                    )}
                >
                    {desktopSidebarOpen ? (
                        <FullWidthNav
                            isDesktopScreenSize={true}
                            closeDrawer={closeDrawer}
                            setSmallScreenDrawOpen={setSmallScreenDrawOpen}
                            setDesktopSidebarOpen={setDesktopSidebarOpen}
                        />
                    ) : (
                        <IconOnlyNav
                            isDesktopScreenSize={true}
                            smallScreenDrawOpen={smallScreenDrawOpen}
                            setSmallScreenDrawOpen={setSmallScreenDrawOpen}
                            setDesktopSidebarOpen={setDesktopSidebarOpen}
                            closeDrawer={closeDrawer}
                        />
                    )}
                </div>
                <div
                    className={cn(
                        'z-0 flex h-dvh max-h-dvh flex-col pl-0 sm:pl-[52px]',
                        desktopSidebarOpen ? 'xl:pl-[250px]' : 'xl:pl-[52px]',
                    )}
                >
                    {/* Mobile top navigation bar */}
                    <MobileNav
                        mobileDrawerOpen={mobileDrawerOpen}
                        setMobileDrawerOpen={setMobileDrawerOpen}
                        closeDrawer={closeDrawer}
                    />
                    {/*
                        We need this container to expand to full available height,
                        because when the MobileNav is visible, it overlays the top
                        part of this container pushing content down.
                    */}
                    <div className="flex grow min-h-0 flex-col lg:rounded-tl-lg">
                        <Outlet />
                    </div>
                </div>
            </div>
            {!disableDrawers && <AllDrawers />}
        </GlobalSearchBarProvider>
    );
};

export const PlatoUserLayout = (): ReactNode => {
    return (
        <AclProvider>
            <AllDrawersProvider>
                <DialogProvider>
                    <PlatoUserLayoutImplementation />
                </DialogProvider>
            </AllDrawersProvider>
        </AclProvider>
    );
};
